/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react/require-default-props */
/* eslint-disable @typescript-eslint/no-use-before-define */
import CheckIconAlt from '@src/svgs/CheckIconAlt';
import React from 'react';
import styled from 'styled-components';

import type { AudienceOption } from './AudienceSelector';

interface AudienceFilterComponentProps {
  audiences: AudienceOption[];
  selectedAudiences: AudienceOption[];
  setSelectedAudiences: (audiences: AudienceOption[]) => void;
  useAudienceFilter: boolean;
  setUseAudienceFilter: (
    useFilter: boolean | ((prev: boolean) => boolean),
  ) => void;
  title?: string;
  align?: 'center' | 'left';
}

const AudienceFilterComponent: React.FC<AudienceFilterComponentProps> = ({
  audiences,
  selectedAudiences,
  setSelectedAudiences,
  useAudienceFilter,
  setUseAudienceFilter,
  title = 'Target Audiences',
  align = 'center',
}) => {
  const handleAudienceToggle = (audience: AudienceOption) => {
    const isSelected = selectedAudiences.some(a => a.id === audience.id);

    if (isSelected) {
      setSelectedAudiences(selectedAudiences.filter(a => a.id !== audience.id));
    } else {
      setSelectedAudiences([...selectedAudiences, audience]);
    }
  };

  return (
    <AudienceFilterSection data-testid="clip-producer-audiences">
      <CheckBoxContainer
        onClick={() => setUseAudienceFilter(prev => !prev)}
        disabled={audiences.length === 0}
        align={align}
      >
        <CheckBox isChecked={useAudienceFilter} role="button">
          {useAudienceFilter && <CheckIconAlt />}
        </CheckBox>
        <div className="checkbox-text">{title}</div>
      </CheckBoxContainer>

      {useAudienceFilter && audiences.length > 0 && (
        <>
          <HelperText align={align}>Select all that apply</HelperText>
          <AudienceGrid>
            {audiences.map(audience => {
              const isSelected = selectedAudiences.some(
                a => a.id === audience.id,
              );
              return (
                <AudienceItem
                  key={audience.id}
                  selected={isSelected}
                  onClick={() => handleAudienceToggle(audience)}
                >
                  <CheckboxIndicator selected={isSelected}>
                    {isSelected && <CheckIconAlt />}
                  </CheckboxIndicator>
                  <AudienceLabel>{audience.label}</AudienceLabel>
                </AudienceItem>
              );
            })}
          </AudienceGrid>
        </>
      )}
    </AudienceFilterSection>
  );
};

export default AudienceFilterComponent;

// Styled components
const AudienceFilterSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CheckBoxContainer = styled.div<{
  disabled: boolean;
  align: 'center' | 'left';
}>`
  display: flex;
  align-items: center;
  justify-content: ${props => props.align};
  gap: 8px;
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};

  .checkbox-text {
    color: var(--foreground);
    font-size: var(--text-xs);
    line-height: 140%;
    text-align: ${props => props.align};
  }
`;

const CheckBox = styled.div<{ isChecked: boolean }>`
  background-color: ${props =>
    props.isChecked ? 'var(--accent)' : 'transparent'};
  width: 16px;
  height: 16px;
  border-radius: 2px;
  overflow: hidden;
  border: 1px solid
    ${props => (props.isChecked ? 'var(--accent)' : 'var(--text-muted)')};
  box-sizing: border-box;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const AudienceGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const AudienceItem = styled.div<{ selected: boolean }>`
  padding: 8px 12px;
  border-radius: 8px;
  box-sizing: border-box;
  border: 1px solid var(--border);
  background-color: var(--surface);
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: var(--surface-elevated);
    border-color: var(--border);
  }
`;

const AudienceLabel = styled.span`
  color: var(--foreground);
  font-size: var(--text-xs);
  /* Normal, per the doc's "row titles are normal weight, not medium" — at 500
     these option rows read heavier than the section label above them. */
  font-weight: var(--font-weight-normal);
  line-height: 1.3;
  flex: 1;
`;

const HelperText = styled.div<{ align: 'center' | 'left' }>`
  color: var(--text-muted);
  font-size: var(--text-xs);
  line-height: 140%;
  text-align: ${props => props.align};
`;

const CheckboxIndicator = styled.div<{ selected: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 2px;
  border: 1px solid
    ${props => (props.selected ? 'var(--accent)' : 'var(--text-muted)')};
  background-color: ${props =>
    props.selected ? 'var(--accent)' : 'transparent'};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;
